import type { Event } from './events';
import { hasEventEnded } from './event-time';

export type EventEditActivitySummary = Readonly<{
  registrationCount: number;
  rsvpCount: number;
}>;

export type EventUpdateReason = 'time' | 'location' | 'route' | 'description';

export type EventEditFields = Pick<
  Event,
  | 'capacity'
  | 'description'
  | 'ends_at'
  | 'payment_methods'
  | 'place_address'
  | 'place_id'
  | 'place_lat'
  | 'place_lng'
  | 'place_name'
  | 'registration_closes_at'
  | 'registration_price_thb'
  | 'revenuecat_offering_id'
  | 'revenuecat_package_id'
  | 'route_version_id'
  | 'starts_at'
  | 'thai_qr_merchant_ref'
>;

export const EMPTY_EVENT_EDIT_ACTIVITY: EventEditActivitySummary = {
  registrationCount: 0,
  rsvpCount: 0,
};

export function isEventEditable(
  event: Pick<Event, 'ends_at' | 'starts_at' | 'status'>,
  now: Date
): boolean {
  return event.status === 'draft' || !hasEventEnded(event, now);
}

export function eventHasEditActivity(activity: EventEditActivitySummary): boolean {
  return activity.registrationCount > 0 || activity.rsvpCount > 0;
}

export function validateCapacityEdit(
  currentCapacity: number | null,
  nextCapacity: number | null,
  activity: EventEditActivitySummary
): string | null {
  if (!eventHasEditActivity(activity) || nextCapacity === currentCapacity) {
    return null;
  }

  if (nextCapacity == null) {
    return null;
  }

  if (currentCapacity == null) {
    return 'Capacity is already unlimited. After registration starts, do not change it to a fixed limit.';
  }

  if (nextCapacity <= currentCapacity) {
    return 'Capacity can only increase after registration starts.';
  }

  return null;
}

export function paymentSettingsChanged(current: EventEditFields, next: EventEditFields): boolean {
  return (
    current.registration_price_thb !== next.registration_price_thb ||
    !samePaymentMethods(current.payment_methods, next.payment_methods) ||
    nullableText(current.revenuecat_offering_id) !== nullableText(next.revenuecat_offering_id) ||
    nullableText(current.revenuecat_package_id) !== nullableText(next.revenuecat_package_id) ||
    nullableText(current.thai_qr_merchant_ref) !== nullableText(next.thai_qr_merchant_ref)
  );
}

export function validatePaymentSettingsEdit(
  current: EventEditFields,
  next: EventEditFields,
  activity: EventEditActivitySummary
): string | null {
  if (!eventHasEditActivity(activity) || !paymentSettingsChanged(current, next)) {
    return null;
  }

  return 'Payment settings cannot change after registration starts.';
}

export function validateEventPostRegistrationEdit(
  current: EventEditFields,
  next: EventEditFields,
  activity: EventEditActivitySummary
): string | null {
  return (
    validateCapacityEdit(current.capacity, next.capacity, activity) ??
    validatePaymentSettingsEdit(current, next, activity)
  );
}

export function eventUpdateReasons(
  current: EventEditFields,
  next: EventEditFields
): EventUpdateReason[] {
  const reasons: EventUpdateReason[] = [];

  if (
    current.starts_at !== next.starts_at ||
    current.ends_at !== next.ends_at ||
    current.registration_closes_at !== next.registration_closes_at
  ) {
    reasons.push('time');
  }

  if (
    nullableText(current.place_id) !== nullableText(next.place_id) ||
    current.place_name !== next.place_name ||
    current.place_address !== next.place_address ||
    current.place_lat !== next.place_lat ||
    current.place_lng !== next.place_lng
  ) {
    reasons.push('location');
  }

  if (current.description !== next.description) {
    reasons.push('description');
  }

  if (current.route_version_id !== next.route_version_id) {
    reasons.push('route');
  }

  return reasons;
}

export function eventUpdatePromptCopy(reasons: readonly EventUpdateReason[]): string | null {
  if (reasons.length === 0) {
    return null;
  }

  const changed = reasons.map(updateReasonLabel).join(', ');
  return `${changed} changed. Send an event update so registered runners can adjust plans and calendars.`;
}

function nullableText(value: string | null): string {
  return value ?? '';
}

function samePaymentMethods(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((method, index) => method === right[index]);
}

function updateReasonLabel(reason: EventUpdateReason): string {
  switch (reason) {
    case 'description':
      return 'Description';
    case 'location':
      return 'Location';
    case 'route':
      return 'Route';
    case 'time':
      return 'Time';
  }
}
